import { useMutation } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { appAPI } from '../../utils/api';

export type UseUserDevicePushTokens = {
    registerDevicePushToken: (devicePushToken: string) => void;
    /**
     * Fires off an API request to delete the device push token but does not await a response.
     *
     * This is so we don't block the user from logging out if the request fails or takes a while.
     */
    deleteDevicePushToken: () => Promise<void>;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

let registeredDevicePushToken: string | null = null;
export default function useUserDevicePushTokens(): UseUserDevicePushTokens {
    const registerDevicePushTokenMutation = useMutation({
        mutationFn: async (devicePushToken: string) => {
            registeredDevicePushToken = devicePushToken;
            const body: APITypes.nativeNotificationService.CreateUserDevicePushTokenRequest = {
                devicePushToken,
            };
            return await appAPI.post(urls.api.nativeNotifications.userDevicePushTokens, {
                body,
            });
        },
    });

    const deleteDevicePushTokenMutation = useMutation({
        mutationFn: async () => {
            if (!registeredDevicePushToken) return;

            const body: APITypes.nativeNotificationService.DeleteUserDevicePushTokenRequest = {
                devicePushToken: registeredDevicePushToken,
            };
            appAPI.post(urls.api.nativeNotifications.removeDevicePushTokens, {
                body,
            });

            // give a bit of a delay so that auth headers aren't cleared before the request is sent
            await sleep(100);

            return;
        },
        onError: (error) => {
            console.error('Failed to delete device push token', error);
        },
    });

    return {
        registerDevicePushToken: registerDevicePushTokenMutation.mutate,
        deleteDevicePushToken: deleteDevicePushTokenMutation.mutateAsync,
    };
}
